
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Globe, Code, PenTool, Camera, Cpu, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Service, getServices } from "@/lib/api";

const iconMap = {
  Globe: <Globe className="h-10 w-10 text-brand-black" />,
  Code: <Code className="h-10 w-10 text-brand-yellow" />,
  PenTool: <PenTool className="h-10 w-10 text-brand-black" />,
  Camera: <Camera className="h-10 w-10 text-brand-yellow" />,
  Cpu: <Cpu className="h-10 w-10 text-brand-black" />,
  ShieldCheck: <ShieldCheck className="h-10 w-10 text-brand-yellow" />,
};

const getIconComponent = (iconName: string) => {
  return iconMap[iconName as keyof typeof iconMap] || <Globe className="h-10 w-10 text-brand-black" />;
};

const ServiceSection = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const data = await getServices();
        setServices(data);
      } catch (error) {
        console.error("Error loading services:", error);
        // Fallback to default services if loading fails
        setServices([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadServices();
  }, []);

  // Default services as fallback
  const defaultServices = [
    {
      id: "1",
      icon_name: "Globe",
      title: "Website Building",
      description:
        "Custom responsive websites, e-commerce solutions, and landing pages designed with your brand in mind.",
      path: "/services/websites",
    },
    {
      id: "2",
      icon_name: "Code",
      title: "App Development",
      description:
        "Native and cross-platform mobile applications for iOS and Android with cutting-edge features.",
      path: "/services/apps",
    },
    {
      id: "3",
      icon_name: "PenTool",
      title: "AI Filters",
      description:
        "Custom AI-powered filters for social media platforms and image processing solutions.",
      path: "/services/ai",
    },
    {
      id: "4",
      icon_name: "Camera",
      title: "Camera Systems",
      description:
        "Advanced security and monitoring camera systems for residential and commercial properties.",
      path: "/services/cameras",
    },
    {
      id: "5",
      icon_name: "Cpu",
      title: "Custom PC Building",
      description:
        "High-performance custom PCs tailored for gaming, content creation, or business applications.",
      path: "/services/pc",
    },
    {
      id: "6",
      icon_name: "ShieldCheck",
      title: "Technical Support",
      description:
        "Comprehensive support for all our products and services to ensure optimal performance.",
      path: "#services",
    },
  ];

  const displayedServices = services.length > 0 ? services : defaultServices;

  return (
    <section id="services" className="py-20 bg-muted/50 min-h-[800px]">
      <div className="container">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t("services.title")}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t("services.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            Array(6).fill(0).map((_, index) => (
              <Card key={index} className="border-border h-[280px]">
                <CardHeader>
                  <div className="h-10 w-10 bg-muted animate-pulse rounded mb-4"></div>
                  <CardTitle className="h-6 bg-muted animate-pulse rounded w-2/3"></CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="h-16 bg-muted animate-pulse rounded"></CardDescription>
                </CardContent>
                <CardFooter>
                  <div className="h-6 bg-muted animate-pulse rounded w-24"></div>
                </CardFooter>
              </Card>
            ))
          ) : (
            displayedServices.map((service) => (
              <Card
                key={service.id}
                className="border-border hover:border-brand-yellow transition-all duration-200 hover:shadow-md"
              >
                <CardHeader>
                  <div className="mb-4">
                    {getIconComponent(service.icon_name)}
                  </div>
                  <CardTitle>{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardContent>
                <CardFooter>
                  <Button
                    variant="ghost"
                    className="p-0 hover:bg-transparent hover:text-brand-yellow"
                  >
                    {t("services.learnMore")}{" "}
                    <ArrowRight className="ml-2 h-4 w-4 rtl:rotate-180 rtl:mr-2" />
                  </Button>
                </CardFooter>
              </Card>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default ServiceSection;
